import { Injectable } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import {
  ClinicInformationForm,
  DoctorInformationForm,
  PatientInformationForm,
  PsychoInformationForm,
} from "../../common/auth";
import { UpdateDoctorDto, UpdatePatientDto, UpdatePsychoDto } from "../../api/models";
import { ageRangeValidator } from "../../common/validators/age-range.validator";

@Injectable({
  providedIn: "root",
})
export class UserInformationFormService {
  /**
   * Builds the user information form.
   * @param group The group of the user.
   * @returns The user information form.
   */
  public getForm(group: string): FormGroup<any> {
    switch (group.toLowerCase()) {
      case "doctor":
        return this._buildDoctorForm();
      case "patient":
        return this._buildPatientForm();
      case "clinic":
        return this._buildClinicForm();
      case "psycho":
        return this._buildPsychoForm();
      default:
        throw new Error(`Gruppo utente non gestito: ${group}`);
    }
  }

  /**
   * Builds the doctor form.
   * @returns The doctor form.
   */
  private _buildDoctorForm(): FormGroup<DoctorInformationForm> {
    return new FormGroup<DoctorInformationForm>({
      firstName: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      lastName: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      email: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      personalEmail: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      gender: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      taxCode: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(
            /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i,
          ),
        ],
      }),
      specialization: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      doctorCode: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      phone: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^(\+39)?\s?\d{9,10}$/),
        ],
      }),
      vatNumber: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^[0-9]{11}$/)],
      }),
      billingAddress: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^.{5,}$/)],
      }),
      description: new FormControl<string>("", {
        nonNullable: true,
      }),
      age: new FormControl<number>(0, {
        nonNullable: true,
        validators: [ageRangeValidator()],
      }),
      yearsOfExperience: new FormControl<number>(0, {
        nonNullable: true,
        validators: [ageRangeValidator(1)],
      }),
      region: new FormControl<string>("", {
        nonNullable: true,
      }),
    });
  }

  /**
   * Builds the psycho form.
   * @returns The psycho form.
   */
  private _buildPsychoForm(): FormGroup<PsychoInformationForm> {
    return new FormGroup<PsychoInformationForm>({
      firstName: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      lastName: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      email: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      gender: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      taxCode: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(
            /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i,
          ),
        ],
      }),
      phone: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^(\+39)?\s?\d{9,10}$/),
        ],
      }),
      specialization: new FormControl<string>("PSYCHO", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      vatNumber: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^[0-9]{11}$/)],
      }),
      billingAddress: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^.{5,}$/)],
      }),
      description: new FormControl<string>("", {
        nonNullable: true,
      }),
      age: new FormControl<number>(0, {
        nonNullable: true,
        validators: [ageRangeValidator()],
      }),
      yearsOfExperience: new FormControl<number>(0, {
        nonNullable: true,
        validators: [ageRangeValidator(1)],
      }),
      region: new FormControl<string>("", {
        nonNullable: true,
      }),
    });
  }

  /**
   * Builds the patient form.
   * @returns The patient form.
   */
  private _buildPatientForm(): FormGroup<PatientInformationForm> {
    return new FormGroup<PatientInformationForm>({
      firstName: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      lastName: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      email: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      gender: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required],
      }),
      taxCode: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(
            /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/i,
          ),
        ],
      }),
      phone: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^(\+39)?\s?\d{9,10}$/),
        ],
      }),
      age: new FormControl<number>(0, {
        nonNullable: true,
        validators: [ageRangeValidator()],
      }),
      region: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required], // ← Aggiunto per coerenza con UI (mostra asterisco)
      }),
    });
  }

  /**
   * Builds a form group for clinic information.
   * @returns A FormGroup instance representing the clinic information form.
   */
  private _buildClinicForm(): FormGroup<ClinicInformationForm> {
    return new FormGroup({
      name: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      city: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ'\-\s]{2,}$/),
        ],
      }),
      street: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[A-Za-zÀ-ÿ0-9'\-\s,\.]{2,}$/),
        ],
      }),
      zipCode: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^[0-9]{5}$/)],
      }),
      phone: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^(\+39)?\s?\d{9,10}$/),
        ],
      }),
      email: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.email],
      }),
      website: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.pattern(
            /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
          ),
        ],
      }),
      description: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.maxLength(500)],
      }),
    });
  }

  /**
   * Builds a DTO object based on the form data.
   * @param group The group of the form.
   * @param form The form group.
   * @returns The DTO object.
   */
  public getDtoByForm(
    group: string,
    form: FormGroup,
  ): UpdatePatientDto | UpdateDoctorDto | UpdatePsychoDto | any {
    switch (group) {
      case "patient":
        return this._buildPatientDto(form);
      case "doctor":
        return this._buildDoctorDto(form);
      case "psycho":
        return this._buildPsychoDto(form);
      case "clinic":
        return this._buildClinicDto(form);
      default:
        throw new Error(`Invalid group: ${group}`);
    }
  }

  /**
   * Builds a DTO object based on the form data.
   * @param form The form group.
   * @returns The DTO object.
   */
  private _buildPatientDto(form: FormGroup): UpdatePatientDto {
    return {
      firstName: form.value.firstName,
      lastName: form.value.lastName,
      gender: form.value.gender,
      taxCode: form.value.taxCode,
      phone: form.value.phone,
      email: form.value.email,
    };
  }

  /**
   * Extract common professional fields from form
   * @param form The form group
   * @returns Common professional fields
   */
  private _extractProfessionalFields(form: FormGroup): any {
    return {
      firstName: form.value.firstName,
      lastName: form.value.lastName,
      gender: form.value.gender,
      taxCode: form.value.taxCode,
      phone: form.value.phone,
      email: form.value.email,
      personalEmail: form.value.personalEmail,
      vatNumber: form.value.vatNumber,
      billingAddress: form.value.billingAddress,
      age: form.value.age,
      description: form.value.description,
      yearsOfExperience: form.value.yearsOfExperience,
      region: form.value.region,
    };
  }

  /**
   * Builds a DTO object based on the form data.
   * @param form The form group.
   * @returns The DTO object.
   */
  private _buildDoctorDto(form: FormGroup): UpdateDoctorDto {
    return {
      ...this._extractProfessionalFields(form),
      specialization: form.value.specialization,
      doctorCode: form.value.doctorCode,
    };
  }

  /**
   * Builds a DTO object based on the form data.
   * @param form The form group.
   * @returns The DTO object.
   */
  private _buildPsychoDto(form: FormGroup): UpdatePsychoDto {
    const { personalEmail, ...professionalFields } = this._extractProfessionalFields(form);
    return {
      ...professionalFields,
      specialization: 'PSYCHO',
    };
  }

  /**
   * Builds a DTO object based on the form data.
   * @param form The form group.
   * @returns The DTO object.
   */
  private _buildClinicDto(form: FormGroup): any {
    return {
      name: form.value.name,
      city: form.value.city,
      street: form.value.street,
      zipCode: form.value.zipCode,
      phone: form.value.phone,
      email: form.value.email,
      contactEmail: form.value.contactEmail,
      website: form.value.website,
      description: form.value.description,
    };
  }
}
